import axios from 'axios'
import { debounce } from 'throttle-debounce'
import gsap from 'gsap'

const search = {
  el: {
    search: {
      background: document.querySelector<HTMLElement>('.search__background'),
      body: document.querySelector<HTMLElement>('.search__body'),
      closeButton: document.querySelector<HTMLElement>('.search__close-button'),
      head: document.querySelector<HTMLElement>('.search__head'),
      input: document.querySelector<HTMLInputElement>('.search__input input'),
      modal: document.querySelector<HTMLElement>('.search__modal'),
      resultsButton: document.querySelector<HTMLElement>('.search__results-button'),
      root: document.querySelector<HTMLElement>('.search'),
    },
    trigger: {
      desktop: document.querySelector<HTMLElement>('.header__search-trigger'),
      mobile: document.querySelector<HTMLElement>('.mobile-header__search-trigger'),
    },
    tabs: {
      buttons: document.querySelectorAll<HTMLElement>('.search .tabs__button'),
      content: document.querySelectorAll<HTMLElement>('.search .tabs-content'),
    },
  },

  init(): void {
    ;[this.el.search.background, this.el.search.closeButton, this.el.trigger.desktop, this.el.trigger.mobile].forEach((trigger) => {
      trigger.addEventListener('click', this.toggleSearchVisibility.bind(this))
    })

    this.el.search.resultsButton.addEventListener('mouseover', this.handleResultsButtonHover.bind(this))
    this.el.search.resultsButton.addEventListener('mouseout', this.handleResultsButtonHover.bind(this))

    this.el.search.input.addEventListener(
      'keyup',
      debounce(66, (event) => {
        event.preventDefault()

        if (event.target.value.length >= 3) {
          this.getSearchResults(event.target.value).then((response) => {
            this.renderSearchResults(response.data)
          })
        }

        this.toggleSearchResultsVisibility(event)
      })
    )
  },

  areResultsVisible(): boolean {
    return this.el.search.root.classList.contains('search--results-visible')
  },

  closeOnEscPress(event): void {
    if (event.key.match('Escape') && this.el.search.root.classList.contains('search--active')) {
      this.toggleSearchVisibility(null)
    }
  },

  async getSearchResults(input: string): Promise<any> {
    const host = window.location.origin
    const query = '/umbraco/api/search/search?query='
    const url = host.concat(query, input)

    return await axios
      .get(url)
      .then((response) => {
        return response
      })
      .catch((err) => console.log(err))
  },

  handleResultsButtonHover(): void {
    this.el.search.modal.classList.toggle('search__modal--results-button-hovered')
  },

  renderSearchResults(data): void {
    const results = [data.treatmentResults, data.cureResults, data.productResults, data.otherResults]
    const total = data.totalCures + data.totalProducts + data.totalTreatments + data.totalOther

    // Rendering amount of results in each tab button
    this.el.tabs.buttons.forEach((button, index) => {
      if (results[index].length > 0) {
        button.classList.add('tabs__button--count')
        button.querySelector('.tabs__button-count').innerHTML = results[index].length
      } else {
        button.classList.remove('tabs__button--count')
        button.querySelector('.tabs__button-count').innerHTML = '0'
      }
    })

    // Rendering results
    this.el.tabs.content.forEach((content, index) => {
      const list = content.firstElementChild
      const noResultsText = this.el.search.root.querySelector<HTMLInputElement>('[name="no-results-text"]').value

      list.innerHTML = ''

      if (results[index].length > 0) {
        results[index].forEach((result) => {
          list.insertAdjacentHTML(
            'beforeend',
            `
            <li class="search__result">
              <a href="${result.url}">
                <h3 class="body1">${result.name}</h3>
                ${result.summary ? `<p class="body2">${result.summary}</p>` : ''}
              </a>
            </li>
          `
          )
        })
      } else {
        list.innerHTML = `<li class="body2" tabindex="0">${noResultsText}</li>`
      }
    })

    // Jumping to the first tab with results
    let jumped = false

    this.el.tabs.buttons.forEach((button, index) => {
      if (results[index].length > 0 && !jumped) {
        button.click()
        jumped = true
      }
    })

    // Updating the redirect link for all results to be displayed
    this.el.search.resultsButton.setAttribute('href', `${this.el.search.head.getAttribute('action')}?q=${this.el.search.input.value}`)

    // Rendering total amount of results in the results button
    if (total > 0) {
      this.el.search.resultsButton.classList.remove('search__results-button--hidden')
      this.el.search.resultsButton.querySelector('.button__text span').innerHTML = total.toString()
    } else {
      this.el.search.resultsButton.classList.add('search__results-button--hidden')
    }
  },

  toggleSearchVisibility(event): void {
    event && event.preventDefault()

    if (this.el.search.root.classList.contains('search--active')) {
      this.el.search.root.classList.remove('search--active')
      this.el.trigger.desktop.ariaExpanded = 'false'
      this.el.trigger.mobile.ariaExpanded = 'false'
      window.removeEventListener('keydown', this.closeOnEscPress.bind(this))
      setTimeout(() => {
        this.el.search.input.value = ''
        this.el.search.root.classList.remove('search--results-visible')
      }, 345)
    } else {
      this.el.search.root.classList.add('search--active')
      this.el.trigger.desktop.ariaExpanded = 'true'
      this.el.trigger.mobile.ariaExpanded = 'true'
      window.addEventListener('keydown', this.closeOnEscPress.bind(this))
      setTimeout(() => {
        this.el.search.input.focus()
      }, 678)
    }
  },

  toggleSearchResultsVisibility(event): void {
    const runAnimation = (action) => {
      const props = {
        clearProps: 'all',
        duration: 0.543,
        ease: 'power3.inOut',
      }

      if (gsap.isTweening(this.el.search.body)) {
        return
      }

      if (!this.areResultsVisible() && action === 'open') {
        gsap.fromTo(
          this.el.search.body,
          {
            height: 0,
          },
          {
            clearProps: 'all',
            delay: 0.06, // Tiny delay
            height: 'auto',
            onStart: () => {
              this.el.search.root.classList.add('search--results-visible')
            },
            ...props,
          }
        )
      }

      if (this.areResultsVisible() && action === 'close') {
        gsap.fromTo(
          this.el.search.body,
          {
            height: this.el.search.body.offsetHeight,
          },
          {
            clearProps: 'all',
            height: 0,
            // onComplete: () => { // This results in jerky transition between opened modal and search bar.
            //   this.el.search.root.classList.remove('search--results-visible')
            // },
            onStart: () => {
              // Delay from start gives smoother transition when going from opened modal to a search bar.
              gsap.delayedCall(0.343, () => {
                this.el.search.root.classList.remove('search--results-visible')
              })
            },
            ...props,
          }
        )
      }
    }

    runAnimation(event.target.value.length >= 3 ? 'open' : 'close')
  },
}

export default search
